// src/components/auth/LoginComPerfil.js
import React, { useState, useEffect } from 'react';
import { useToast } from '@chakra-ui/react';

/* ---------- SVG Icons ---------- */
const EyeIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const EyeOffIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
        <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
);
const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
    </svg>
);
const LockIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
);
const ArrowRightIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
    </svg>
);
const ArrowLeftIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="15 18 9 12 15 6" />
    </svg>
);
const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12" />
    </svg>
);

/* ---------- Componente Principal ---------- */
const LoginComPerfil = ({ supabase, onLoginSuccess }) => {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [focusedField, setFocusedField] = useState(null);
    const [mounted, setMounted] = useState(false);
    const [showSenhaModal, setShowSenhaModal] = useState(false);
    const toast = useToast();

    useEffect(() => {
        const t = setTimeout(() => setMounted(true), 60);
        return () => clearTimeout(t);
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        if (!email || !senha) {
            toast({ title: 'Preencha email e senha.', status: 'warning', duration: 3000, isClosable: true, position: 'top' });
            return;
        }
        setIsLoading(true);
        try {
            // 1. Verificar credenciais server-side (bcrypt via pgcrypto, sem expor senha_hash ao cliente)
            const { data: loginResult, error: loginError } = await supabase
                .rpc('verificar_login', { p_email: email, p_senha: senha });
            if (loginError) throw new Error(loginError.message);
            if (!loginResult || loginResult.length === 0) throw new Error('Credenciais inválidas');

            const r = loginResult[0];
            const usuario = {
                id: r.v_id, nome: r.v_nome, email: r.v_email, perfil: r.v_perfil,
                cliente_id: r.v_cliente_id, auth_user_id: r.v_auth_user_id,
                cliente_nome: r.v_cliente_nome, cliente_ativo: r.v_cliente_ativo,
                cliente_expiracao: r.v_cliente_expiracao,
            };

            // 2. Verificar licença (servidor já garantiu, mas bloqueia antes de criar sessão Auth)
            if (usuario.perfil !== 'super_admin') {
                if (!usuario.cliente_ativo) throw new Error('Licença do cliente está inativa.');
                if (usuario.cliente_expiracao && new Date() > new Date(usuario.cliente_expiracao))
                    throw new Error('Licença do cliente expirou.');
            }

            const emailAuth = `user${usuario.id.replace(/-/g, '').substring(0, 12)}@sistema.app`;

            // 3. Autenticar no Supabase Auth
            let authData = null;
            const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({ email: emailAuth, password: senha });
            if (!signInError && signInData?.user) {
                authData = signInData;
            } else {
                const { data: signUpData, error: signUpError } = await supabase.auth.signUp({
                    email: emailAuth, password: senha, options: { data: {}, emailRedirectTo: undefined }
                });
                if (signUpError?.message?.match(/already registered|email exists|User already registered/i))
                    throw new Error('Senha alterada pelo administrador. Peça para redefinir seu acesso.');
                if (signUpError || !signUpData?.user)
                    throw new Error(`Erro na autenticação: ${signUpError?.message || 'Tente novamente.'}`);

                await supabase.rpc('confirmar_auth_usuario', { p_email: emailAuth });
                await new Promise(r => setTimeout(r, 800));

                const { data: s2, error: e2 } = await supabase.auth.signInWithPassword({ email: emailAuth, password: senha });
                if (!e2 && s2?.user) authData = s2;
                else if (signUpData.session) authData = signUpData;
                else throw new Error('Conta criada mas login automático falhou. Tente novamente.');
            }
            if (!authData?.user) throw new Error('Falha na autenticação. Tente novamente.');

            // 4. Gravar perfil/cliente_id em app_metadata (não editável pelo usuário — protege RLS)
            await supabase.rpc('definir_metadata_auth');

            // 5. Atualizar user_metadata para exibição no frontend
            const updateMeta = { usuario_sistema_id: usuario.id, perfil: usuario.perfil, nome: usuario.nome, email_real: usuario.email };
            if (usuario.perfil !== 'super_admin' && usuario.cliente_id) {
                updateMeta.cliente_id = usuario.cliente_id;
                updateMeta.cliente_nome = usuario.cliente_nome;
            }
            await supabase.auth.updateUser({ data: updateMeta });

            // 6. Renovar JWT para incluir app_metadata atualizado nas próximas requisições
            await new Promise(r => setTimeout(r, 800));
            await supabase.auth.refreshSession();

            const clienteData = usuario.perfil !== 'super_admin'
                ? { id: usuario.cliente_id, nome: usuario.cliente_nome, ativo: usuario.cliente_ativo, data_expiracao: usuario.cliente_expiracao }
                : null;

            toast({ title: 'Login realizado!', description: `Bem-vindo(a), ${usuario.nome}`, status: 'success', duration: 3000, isClosable: true, position: 'top' });
            if (onLoginSuccess) onLoginSuccess({ usuario, cliente: clienteData, perfil: usuario.perfil });
        } catch (error) {
            console.error('Erro no login:', error);
            toast({ title: 'Erro no login', description: error.message || 'Verifique suas credenciais.', status: 'error', duration: 5000, isClosable: true, position: 'top' });
        } finally {
            setIsLoading(false);
        }
    };

    /* ---------- Render ---------- */
    return (
        <>
            {/* ====== ESTILOS GLOBAIS ====== */}
            <style>{`
                @keyframes spinLoader { to { transform: rotate(360deg); } }
                @keyframes aurora1 { 0%,100% { transform: translate(0,0) scale(1); opacity:0.7; } 50% { transform: translate(60px,-40px) scale(1.08); opacity:1; } }
                @keyframes aurora2 { 0%,100% { transform: translate(0,0) scale(1); opacity:0.6; } 50% { transform: translate(-50px,60px) scale(1.05); opacity:0.9; } }
                @keyframes aurora3 { 0%,100% { transform: translate(0,0) scale(1); opacity:0.5; } 50% { transform: translate(30px,30px) scale(1.1); opacity:0.8; } }
                @keyframes cardIn { from { opacity:0; transform:translateY(32px) scale(0.97); } to { opacity:1; transform:translateY(0) scale(1); } }

                .login-input { color: #0f172a !important; }
                .login-input::placeholder { color: #94a3b8; }
                .login-input::-webkit-input-placeholder { color: #94a3b8; }
                .btn-login:hover:not(:disabled) { transform: translateY(-2px) !important; box-shadow: 0 10px 30px rgba(44,203,144,0.5) !important; }
                .btn-login:active:not(:disabled) { transform: scale(0.98) !important; }
                .back-link { display:inline-flex; align-items:center; gap:6px; color:rgba(255,255,255,0.55); font-size:13px; font-weight:500; text-decoration:none; font-family:inherit; cursor:pointer; transition:color 0.2s; border:none; background:none; padding:0; }
                .back-link:hover { color:rgba(255,255,255,0.9); }
                .login-page-wrap { padding: 20px 16px; min-height: 100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; }
                .login-card-box { padding: 28px 22px; border-radius: 20px; }
                @media (min-width: 480px) {
                    .login-page-wrap { padding: 32px 24px; }
                    .login-card-box { padding: 36px 32px; border-radius: 24px; }
                }
                @media (min-width: 768px) {
                    .login-card-box { padding: 44px 40px; border-radius: 28px; }
                }
            `}</style>

            <div style={{
                minHeight: '100vh',
                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
                background: '#0A1F3A',
                position: 'relative',
                overflow: 'hidden',
            }}>

                {/* ====== AURORA BACKGROUND ====== */}
                <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0, pointerEvents: 'none' }}>
                    {/* Aurora verde — canto superior esquerdo */}
                    <div style={{
                        position: 'absolute', top: '-20%', left: '-15%',
                        width: '70vw', height: '70vw', maxWidth: '700px', maxHeight: '700px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at center, rgba(44,203,144,0.22) 0%, rgba(44,203,144,0.08) 45%, transparent 70%)',
                        filter: 'blur(80px)',
                        animation: 'aurora1 14s ease-in-out infinite',
                    }} />
                    {/* Aurora azul — canto inferior direito */}
                    <div style={{
                        position: 'absolute', bottom: '-20%', right: '-15%',
                        width: '65vw', height: '65vw', maxWidth: '650px', maxHeight: '650px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at center, rgba(45,184,225,0.2) 0%, rgba(45,184,225,0.06) 45%, transparent 70%)',
                        filter: 'blur(80px)',
                        animation: 'aurora2 18s ease-in-out infinite',
                    }} />
                    {/* Aurora verde suave — centro */}
                    <div style={{
                        position: 'absolute', top: '30%', left: '25%',
                        width: '50vw', height: '50vw', maxWidth: '500px', maxHeight: '500px',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle at center, rgba(44,203,144,0.07) 0%, transparent 65%)',
                        filter: 'blur(60px)',
                        animation: 'aurora3 22s ease-in-out infinite',
                    }} />
                    {/* Grid sutil */}
                    <div style={{
                        position: 'absolute', inset: 0,
                        backgroundImage: 'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
                        backgroundSize: '48px 48px',
                        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%)',
                        WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 20%, transparent 100%)',
                    }} />
                </div>

                {/* ====== BARRA SUPERIOR ====== */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '20px 28px',
                    zIndex: 10,
                }}>
                    {/* Logo */}
                    <div style={{ display: 'inline-flex', alignItems: 'center', background: '#fff', borderRadius: '10px', padding: '5px 14px', border: '1px solid rgba(255,255,255,0.15)' }}>
                        <img
                            src="/logo-horizontal.png"
                            alt="Evlo"
                            style={{ height: '26px', objectFit: 'contain' }}
                            onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = '<span style="font-size:18px;font-weight:800;color:#0E2C52;letter-spacing:-0.03em">evlo</span>'; }}
                        />
                    </div>

                    {/* Voltar ao site */}
                    <a href="/" className="back-link">
                        <ArrowLeftIcon />
                        Voltar ao site
                    </a>
                </div>

                {/* ====== CONTEÚDO CENTRAL ====== */}
                <div className="login-page-wrap" style={{ position: 'relative', zIndex: 1 }}>

                    {/* Badge */}
                    <div style={{
                        display: 'inline-flex', alignItems: 'center', gap: '7px',
                        padding: '5px 14px', borderRadius: '100px',
                        background: 'rgba(44,203,144,0.12)',
                        border: '1px solid rgba(44,203,144,0.25)',
                        marginBottom: '24px',
                        opacity: mounted ? 1 : 0,
                        transition: 'opacity 0.6s ease 0.1s',
                    }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#2CCB90', flexShrink: 0, boxShadow: '0 0 6px rgba(44,203,144,0.8)' }} />
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#2CCB90', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                            Área restrita
                        </span>
                    </div>

                    {/* Card */}
                    <div
                        className="login-card-box"
                        style={{
                            width: '100%',
                            maxWidth: '420px',
                            background: 'rgba(255,255,255,0.97)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255,255,255,0.12)',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.3), 0 30px 80px rgba(0,0,0,0.4), 0 0 0 1px rgba(44,203,144,0.1)',
                            animation: mounted ? 'cardIn 0.7s cubic-bezier(0.16,1,0.3,1) forwards' : 'none',
                            opacity: mounted ? 1 : 0,
                        }}
                    >
                        {/* Header */}
                        <div style={{ marginBottom: '28px' }}>
                            <h2 style={{
                                fontSize: '22px', fontWeight: 800, margin: '0 0 6px',
                                color: '#0A1F3A', letterSpacing: '-0.03em',
                            }}>
                                Bem-vindo de volta
                            </h2>
                            <p style={{ fontSize: '14px', color: '#64748b', margin: 0, lineHeight: 1.5 }}>
                                Entre com suas credenciais para acessar o sistema
                            </p>
                        </div>

                        {/* Linha decorativa */}
                        <div style={{
                            height: '2px',
                            background: 'linear-gradient(90deg, #2CCB90, #2DB8E1)',
                            borderRadius: '2px',
                            marginBottom: '28px',
                            opacity: 0.6,
                        }} />

                        {/* Form */}
                        <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

                            {/* Campo Email */}
                            <div>
                                <label style={{
                                    display: 'block', fontSize: '11px', fontWeight: 700,
                                    letterSpacing: '0.06em', textTransform: 'uppercase',
                                    color: '#475569', marginBottom: '7px',
                                }}>
                                    Email
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <span style={{
                                        position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)',
                                        color: focusedField === 'email' ? '#2CCB90' : '#94a3b8',
                                        pointerEvents: 'none', transition: 'color 0.2s',
                                    }}><MailIcon /></span>
                                    <input
                                        className="login-input"
                                        type="email" value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        onFocus={() => setFocusedField('email')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="seu@email.com"
                                        style={{
                                            width: '100%', boxSizing: 'border-box',
                                            padding: '12px 16px 12px 42px',
                                            borderRadius: '11px',
                                            background: focusedField === 'email' ? '#f0fdf8' : '#f8fafc',
                                            border: focusedField === 'email'
                                                ? '1.5px solid #2CCB90'
                                                : '1.5px solid #e2e8f0',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            outline: 'none',
                                            boxShadow: focusedField === 'email' ? '0 0 0 3px rgba(44,203,144,0.1)' : 'none',
                                            transition: 'all 0.2s ease',
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Campo Senha */}
                            <div>
                                <label style={{
                                    display: 'block', fontSize: '11px', fontWeight: 700,
                                    letterSpacing: '0.06em', textTransform: 'uppercase',
                                    color: '#475569', marginBottom: '7px',
                                }}>
                                    Senha
                                </label>
                                <div style={{ position: 'relative' }}>
                                    <span style={{
                                        position: 'absolute', left: '13px', top: '50%', transform: 'translateY(-50%)',
                                        color: focusedField === 'senha' ? '#2CCB90' : '#94a3b8',
                                        pointerEvents: 'none', transition: 'color 0.2s',
                                    }}><LockIcon /></span>
                                    <input
                                        className="login-input"
                                        type={showPassword ? 'text' : 'password'}
                                        value={senha}
                                        onChange={(e) => setSenha(e.target.value)}
                                        onFocus={() => setFocusedField('senha')}
                                        onBlur={() => setFocusedField(null)}
                                        placeholder="••••••••"
                                        style={{
                                            width: '100%', boxSizing: 'border-box',
                                            padding: '12px 46px 12px 42px',
                                            borderRadius: '11px',
                                            background: focusedField === 'senha' ? '#f0fdf8' : '#f8fafc',
                                            border: focusedField === 'senha'
                                                ? '1.5px solid #2CCB90'
                                                : '1.5px solid #e2e8f0',
                                            fontSize: '14px',
                                            fontFamily: 'inherit',
                                            outline: 'none',
                                            boxShadow: focusedField === 'senha' ? '0 0 0 3px rgba(44,203,144,0.1)' : 'none',
                                            transition: 'all 0.2s ease',
                                        }}
                                    />
                                    <button
                                        type="button" onClick={() => setShowPassword(!showPassword)}
                                        aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                        style={{
                                            position: 'absolute', right: '11px', top: '50%', transform: 'translateY(-50%)',
                                            background: 'none', border: 'none', cursor: 'pointer',
                                            color: '#94a3b8', padding: '4px',
                                            transition: 'color 0.15s', borderRadius: '6px',
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#475569'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = '#94a3b8'}
                                    >
                                        {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                    </button>
                                </div>
                            </div>

                            {/* Esqueceu a senha */}
                            <div style={{ textAlign: 'right', marginTop: '-4px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowSenhaModal(true)}
                                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#94a3b8', fontFamily: 'inherit', padding: 0, transition: 'color 0.15s' }}
                                    onMouseEnter={e => e.currentTarget.style.color = '#2CCB90'}
                                    onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}
                                >
                                    Esqueceu sua senha?
                                </button>
                            </div>

                            {/* Botão de Login */}
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="btn-login"
                                style={{
                                    marginTop: '6px',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                                    width: '100%', padding: '14px 24px',
                                    borderRadius: '12px', border: 'none',
                                    background: isLoading
                                        ? 'linear-gradient(135deg, rgba(44,203,144,0.5), rgba(34,184,209,0.5))'
                                        : 'linear-gradient(135deg, #2CCB90 0%, #22b8d1 100%)',
                                    color: '#fff',
                                    fontSize: '14px', fontWeight: 700, fontFamily: 'inherit',
                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                    letterSpacing: '0.01em',
                                    boxShadow: isLoading ? 'none' : '0 4px 20px rgba(44,203,144,0.35)',
                                    transition: 'all 0.25s cubic-bezier(0.16,1,0.3,1)',
                                }}
                            >
                                {isLoading ? (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                                            style={{ animation: 'spinLoader 0.75s linear infinite' }}>
                                            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                                        </svg>
                                        Entrando...
                                    </>
                                ) : (
                                    <>Entrar <ArrowRightIcon /></>
                                )}
                            </button>
                        </form>

                        {/* Divisor */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', margin: '20px 0 16px' }}>
                            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                            <span style={{ fontSize: '11px', color: '#cbd5e1', fontWeight: 500, letterSpacing: '0.04em' }}>ou</span>
                            <div style={{ flex: 1, height: '1px', background: '#e2e8f0' }} />
                        </div>

                        {/* Suporte */}
                        <p style={{ textAlign: 'center', fontSize: '13px', color: '#94a3b8', margin: 0 }}>
                            Precisa de ajuda?{' '}
                            <span
                                style={{ color: '#2CCB90', fontWeight: 600, cursor: 'pointer', transition: 'color 0.15s' }}
                                onMouseEnter={(e) => e.currentTarget.style.color = '#16a972'}
                                onMouseLeave={(e) => e.currentTarget.style.color = '#2CCB90'}
                                onClick={() => setShowSenhaModal(true)}
                            >
                                Falar com suporte
                            </span>
                        </p>
                    </div>

                    {/* Copyright */}
                    <p style={{
                        marginTop: '28px',
                        fontSize: '11px', color: 'rgba(255,255,255,0.25)',
                        fontWeight: 500, letterSpacing: '0.03em',
                        textAlign: 'center',
                    }}>
                        © 2025 Evlo App · Todos os direitos reservados
                    </p>

                </div>
            </div>

            {/* Modal Esqueceu Senha */}
            {showSenhaModal && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}
                    onClick={() => setShowSenhaModal(false)}>
                    <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(6px)' }} />
                    <div
                        style={{ position: 'relative', background: '#ffffff', borderRadius: '20px', padding: '32px', maxWidth: '400px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.18)', border: '1px solid #e2e8f0', fontFamily: "'Inter', -apple-system, sans-serif" }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ width: '48px', height: '48px', borderRadius: '14px', background: 'rgba(44,203,144,0.1)', border: '1px solid rgba(44,203,144,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
                            <LockIcon />
                        </div>
                        <h3 style={{ margin: '0 0 8px', fontSize: '18px', fontWeight: 700, color: '#0f172a' }}>Recuperar acesso</h3>
                        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#64748b', lineHeight: 1.6 }}>
                            Para redefinir sua senha, entre em contato com o <strong style={{ color: '#1e293b' }}>administrador do sistema</strong>. Ele pode redefinir seu acesso pela tela de Gerenciamento de Usuários.
                        </p>
                        <div style={{ padding: '12px 16px', background: 'rgba(44,203,144,0.06)', border: '1px solid rgba(44,203,144,0.2)', borderRadius: '10px', marginBottom: '20px' }}>
                            <p style={{ margin: '0 0 4px', fontSize: '12px', fontWeight: 700, color: '#2CCB90', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Como funciona</p>
                            <p style={{ margin: 0, fontSize: '13px', color: '#64748b', lineHeight: 1.5 }}>O administrador acessa <strong>Usuários → Redefinir Acesso</strong> e você poderá entrar com a nova senha em seguida.</p>
                        </div>
                        <button
                            onClick={() => setShowSenhaModal(false)}
                            style={{ width: '100%', padding: '11px', borderRadius: '10px', background: 'linear-gradient(135deg, #2CCB90, #22b8d1)', border: 'none', color: '#fff', fontSize: '14px', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
                        >
                            Entendido
                        </button>
                    </div>
                </div>
            )}
        </>
    );
};

export default LoginComPerfil;
